import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EVENTS } from "@/mocks/events";
import TopNav from "@/components/feature/TopNav";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import ShareBar from "./components/ShareBar";
import StoryTimeline from "./components/StoryTimeline";

export default function Story() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);

  const index = useMemo(() => EVENTS.findIndex((e) => e.id === id), [id]);
  const event = index >= 0 ? EVENTS[index] : undefined;

  useDocumentMeta({
    title: event ? `${event.title} - 吃瓜资讯` : "专题未找到 - 吃瓜资讯",
    description:
      event?.excerpt ||
      "吃瓜资讯，专注热点事件梳理、深度解析与完整时间线还原。",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(t);
  }, [copied]);

  if (!event) {
    return (
      <>
        <TopNav />
        <main className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 py-28 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-500">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="mt-5 text-2xl font-bold text-foreground-950">没有找到这枚瓜</h1>
          <p className="mt-2 text-sm text-foreground-600">
            该专题可能已下架或地址有误，去首页逛逛其他瓜局吧。
          </p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const prev = index > 0 ? EVENTS[index - 1] : null;
  const next = index < EVENTS.length - 1 ? EVENTS[index + 1] : null;
  const related = [
    ...EVENTS.filter((e) => e.category === event.category && e.id !== event.id),
    ...EVENTS.filter((e) => e.category !== event.category),
  ]
    .slice(0, 3);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const articleUrl = `${origin}/story/${event.id}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: event.title,
    description: event.excerpt,
    datePublished: `${event.published}T09:00:00+08:00`,
    articleSection: event.category,
    inLanguage: "zh-CN",
    image: [event.cover],
    author: { "@type": "Organization", name: "吃瓜资讯 · 采编组" },
    publisher: {
      "@type": "Organization",
      name: "吃瓜资讯",
      logo: {
        "@type": "ImageObject",
        url: origin,
      },
    },
    mainEntityOfPage: articleUrl,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
      {
        "@type": "ListItem",
        position: 2,
        name: event.category,
        item: `${origin}/#latest`,
      },
      { "@type": "ListItem", position: 3, name: event.hotTitle },
    ],
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const toc = [
    { id: "backstory", label: "来龙去脉" },
    { id: "timeline", label: "完整时间线" },
    { id: "truth", label: "背后真相" },
    { id: "analysis", label: "深度解析" },
    { id: "ending", label: "结语" },
  ];

  const scrollToSection = (elId: string) =>
    document.getElementById(elId)?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <TopNav />

      <main>
        {/* 文章头部 */}
        <section className="mx-auto w-full max-w-3xl px-4 pb-2 pt-8 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/#latest" className="hover:text-primary-600">
              {event.category}
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="line-clamp-1 text-foreground-700">{event.hotTitle}</span>
          </nav>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
              {event.category}
            </span>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">
              {event.tag}
            </span>
            <span className="flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
              热度 {event.heat}
            </span>
          </div>

          <h1 className="mt-4 text-2xl font-extrabold leading-snug tracking-tight text-foreground-950 md:text-4xl">
            {event.title}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-primary-700 md:text-lg">
            {event.lead}
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-4">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-edit-2-line" aria-hidden="true"></i>
                </span>
                采编组 · {event.author}
              </span>
              <time dateTime={event.published} className="flex items-center gap-1">
                <i className="ri-calendar-line" aria-hidden="true"></i>
                发布于 {event.published}
              </time>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true"></i>
                约 {event.readingMin} 分钟读完
              </span>
            </div>
            <ShareBar onCopy={copyLink} copied={copied} />
          </div>
        </section>

        {/* 主视觉 */}
        <section className="mx-auto w-full max-w-4xl px-4 pt-6 md:px-6">
          <div className="overflow-hidden rounded-3xl bg-background-100">
            <img
              src={event.cover}
              alt={`${event.title} 事件主视觉封面`}
              className="aspect-[16/9] w-full object-cover object-top"
            />
          </div>
        </section>

        {/* 导读目录 */}
        <aside className="mx-auto mt-6 w-full max-w-3xl px-4 md:px-6">
          <div className="rounded-2xl bg-background-100 p-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
              本文导读
            </span>
            <div className="mt-3 flex flex-wrap gap-2">
              {toc.map((t, idx) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => scrollToSection(t.id)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-3.5 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  <span className="font-semibold text-primary-500">{idx + 1}</span>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <article className="prose-warm mx-auto w-full max-w-3xl px-4 md:px-6">
          {/* 来龙去脉 */}
          <section id="backstory" className="scroll-mt-28 pt-10">
            <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-file-history-line" aria-hidden="true"></i>
              </span>
              来龙去脉 · 事件回顾
            </h2>
            <div className="mt-5 space-y-4">
              {event.overview.map((p, i) => (
                <p key={i} className="text-[15px] leading-[1.95] text-foreground-800">
                  {p}
                </p>
              ))}
            </div>
          </section>

          {/* 完整时间线 */}
          <StoryTimeline items={event.timeline} count={event.timeline.length} />

          {/* 背后真相 */}
          <section
            id="truth"
            className="scroll-mt-28 overflow-hidden rounded-3xl bg-accent-100 pt-8"
          >
            <div className="px-6 md:px-8">
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground-950 md:text-2xl">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                  <i className="ri-eye-2-line" aria-hidden="true"></i>
                </span>
                背后真相
              </h2>
              <h3 className="mt-4 font-display text-lg text-accent-900 md:text-xl">
                {event.truthTitle}
              </h3>
              <div className="mt-3 space-y-3 pb-6">
                {event.truthText.map((t, i) => (
                  <p key={i} className="text-[15px] leading-[1.95] text-foreground-800">
                    {t}
                  </p>
                ))}
              </div>
            </div>
          </section>

          {/* 深度解析 */}
          <section id="analysis" className="scroll-mt-28 pt-10">
            <h2 className="flex items-center gap-2.5 text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary-500 text-background-50">
                <i className="ri-focus-3-line" aria-hidden="true"></i>
              </span>
              深度解析 · 多视角拆解
            </h2>
            <div className="mt-6 space-y-6">
              {event.analysis.map((a, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-background-200 bg-background-100 p-5 md:p-6"
                >
                  <h3 className="flex items-start gap-2.5 text-base font-bold text-foreground-900">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-secondary-200 text-xs font-bold text-secondary-900">
                      {i + 1}
                    </span>
                    {a.title}
                  </h3>
                  <p className="mt-3 text-[15px] leading-[1.95] text-foreground-800">
                    {a.text}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* 结语 */}
          <section
            id="ending"
            className="scroll-mt-28 my-10 rounded-2xl bg-primary-500 p-6 text-background-50 md:p-8"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/20 px-3 py-1 text-xs font-semibold">
              <i className="ri-lightbulb-line" aria-hidden="true"></i>
              编辑结语
            </span>
            <p className="mt-4 text-[15px] leading-[1.95] text-primary-50">{event.conclusion}</p>
          </section>
        </article>

        {/* 相关推荐 */}
        <section className="mx-auto w-full max-w-7xl px-4 pb-12 md:px-6">
          <div className="mt-6 rounded-3xl bg-background-100 p-5 md:p-8">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-foreground-950">继续吃瓜</h2>
              <Link
                to="/#hot"
                className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                返回今日热榜
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/story/${item.id}`}
                  className="group flex flex-col overflow-hidden rounded-2xl bg-background-50 transition-all hover:-translate-y-1"
                >
                  <div className="aspect-[16/10] w-full overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <span className="text-[11px] font-semibold text-primary-600">
                      {item.category} · {item.tag}
                    </span>
                    <h3 className="mt-1.5 line-clamp-2 text-sm font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                      {item.hotTitle}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>

            {/* 上一篇/下一篇 */}
            <div className="mt-6 grid grid-cols-1 gap-3 border-t border-background-200 pt-5 sm:grid-cols-2">
              {prev ? (
                <Link
                  to={`/story/${prev.id}`}
                  className="group flex flex-col rounded-xl bg-background-50 p-4 transition-colors hover:bg-primary-100/60"
                >
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-arrow-left-line"></i> 上一篇
                  </span>
                  <span className="mt-1.5 line-clamp-1 text-sm font-semibold text-foreground-800 group-hover:text-primary-700">
                    {prev.hotTitle}
                  </span>
                </Link>
              ) : (
                <span className="hidden sm:block"></span>
              )}
              {next && (
                <Link
                  to={`/story/${next.id}`}
                  className="group flex flex-col items-end rounded-xl bg-background-50 p-4 text-right transition-colors hover:bg-primary-100/60"
                >
                  <span className="flex items-center gap-1 text-xs text-foreground-500">
                    下一篇 <i className="ri-arrow-right-line"></i>
                  </span>
                  <span className="mt-1.5 line-clamp-1 text-sm font-semibold text-foreground-800 group-hover:text-primary-700">
                    {next.hotTitle}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </>
  );
}